'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { Box, BoxProps } from '@/primitives';
import WithSkeleton from './WithSkeleton';

type ImageWithSkeletonProps = {
  src: string;
  alt: string;
  width: number;
  height: number;
  isLoading?: boolean;
  containerSx?: BoxProps['sx'];
  imageStyle?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
};

export default function ImageWithSkeleton({
  src,
  alt,
  width,
  height,
  isLoading: externalLoading,
  containerSx,
  imageStyle,
  onLoad: externalOnLoad,
  onError: externalOnError,
}: ImageWithSkeletonProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Reset image loaded state when src changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [src]);

  const handleLoad = () => {
    setImageLoaded(true);
    externalOnLoad?.();
  };

  const handleError = () => {
    setImageLoaded(true); // Stop showing skeleton even on error
    setImageError(true);
    externalOnError?.();
  };

  // Show skeleton if externally loading OR if image hasn't loaded yet
  const shouldShowSkeleton = externalLoading || (!!src && !imageLoaded);

  // Don't render if there's an error or no src
  if (imageError || !src) {
    return null;
  }

  return (
    <WithSkeleton
      width={width}
      height={height}
      isLoading={shouldShowSkeleton}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...containerSx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Image
          src={src}
          alt={alt}
          width={width}
          height={height}
          unoptimized
          onLoad={handleLoad}
          onError={handleError}
          style={{
            objectFit: 'contain',
            maxWidth: '100%',
            width: 'auto',
            height: 'auto',
            ...imageStyle,
          }}
        />
      </Box>
    </WithSkeleton>
  );
}
